// Login vendeur (Lot M1) — mobile-first
// Deux entrées :
//   1. Magic-link auto-consommé via URL /vendeur/magic/:token
//   2. Login manuel avec référence + code
// Aucun compte / mot de passe.

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { KeyRound, ArrowRight, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { sellerAuthApi } from "@/lib/api/sellerAuth";

export default function VendeurLogin() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [reference, setReference] = useState(searchParams.get("ref") || "");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [exchanging, setExchanging] = useState(!!token);

  // Consomme le magic-link automatiquement s'il est présent dans l'URL
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await sellerAuthApi.exchangeMagic(token);
        toast.success(`Bienvenue ${r.seller?.name || ""}`);
        if (r.mandate_id) navigate(`/vendeur/mandat/${r.mandate_id}`);
        else navigate("/vendeur");
      } catch (e) {
        toast.error(e.message || "Lien invalide ou expiré");
        setExchanging(false);
      }
    })();
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim() || !code.trim()) {
      return toast.error("Référence et code requis");
    }
    setSubmitting(true);
    try {
      const r = await sellerAuthApi.loginCode(reference.trim(), code.trim());
      toast.success(`Bienvenue ${r.seller?.name || ""}`);
      if (r.mandate_id) navigate(`/vendeur/mandat/${r.mandate_id}`);
      else navigate("/vendeur");
    } catch (e2) {
      toast.error(e2.message || "Référence ou code incorrect");
    } finally {
      setSubmitting(false);
    }
  };

  if (exchanging) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6"
           data-testid="vendeur-login-exchanging">
        <div className="text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.06]">
            <KeyRound size={20} className="text-[#C8FF2F] animate-pulse" />
          </div>
          <div className="mt-5 text-[15px] font-medium">Ouverture de votre espace…</div>
          <p className="mt-1 text-[12.5px] text-white/45">Validation du lien vendeur.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: "linear-gradient(180deg, #060606 0%, #0C0C0C 45%, #131313 100%)",
      }}
      data-testid="vendeur-login-page"
    >
      <div className="mx-auto flex min-h-screen max-w-[440px] flex-col px-6 py-10">
        <div className="flex items-center gap-2.5 text-white">
          <FileSignature size={22} className="text-[#C8FF2F]" strokeWidth={1.7} />
          <span className="text-[14px] font-semibold tracking-[-0.01em]">Espace vendeur</span>
        </div>

        <div className="mt-16">
          <h1 className="text-[30px] font-semibold leading-[1.05] tracking-[-0.02em]">
            Suivez votre<br />véhicule en toute simplicité.
          </h1>
          <p className="mt-3 text-[13.5px] text-white/55 max-w-[360px]">
            Aucun compte à créer. Saisissez votre référence de mandat et
            le code d&apos;accès à 6 caractères transmis par votre agence.
          </p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-3" data-testid="vendeur-login-form">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.16em] text-white/45">
              Référence du mandat
            </span>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value.toUpperCase())}
              placeholder="MND-YYMM-XXXX"
              className="h-12 rounded-xl bg-white/[0.05] px-4 font-mono text-[14px] text-white outline-none transition-colors focus:bg-white/[0.08] focus:ring-2 focus:ring-[#C8FF2F]/40"
              autoCapitalize="characters"
              autoComplete="off"
              data-testid="vendeur-login-reference-input"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.16em] text-white/45">
              Code d&apos;accès
            </span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="6 caractères"
              maxLength={6}
              className="h-12 rounded-xl bg-white/[0.05] px-4 font-mono text-[15px] tracking-[0.3em] text-white outline-none focus:bg-white/[0.08] focus:ring-2 focus:ring-[#C8FF2F]/40"
              autoCapitalize="characters"
              autoComplete="off"
              data-testid="vendeur-login-code-input"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C8FF2F] text-[14px] font-medium text-black transition-transform hover:scale-[1.005] disabled:opacity-40"
            data-testid="vendeur-login-submit-btn"
          >
            {submitting ? "Connexion…" : (<>Accéder à mon mandat <ArrowRight size={14} /></>)}
          </button>
        </form>

        <div className="mt-auto pt-10 text-[11.5px] text-white/35">
          Un problème ? Contactez votre agence : votre code peut être régénéré à
          tout moment sur simple demande.
        </div>
      </div>
    </div>
  );
}
